import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { HttpError } from '@pickleballinc/lib/http-error';

import { OptionExample } from '@/modules/players/v3/types';
import { Rating, User, UserV2 } from '@/types/players.types';
import { mapUserToApiPayload } from '@/utils/helpers/mapUserToApiPayload';

export const genderOptions: OptionExample[] = [
  {
    id: '0',
    label: formatText({ text: 'Male', role: TextRole.RadioLabel }),
    value: 'M',
  },
  {
    id: '1',
    label: formatText({ text: 'Female', role: TextRole.RadioLabel }),
    value: 'F',
  },
] as const;

export const handOptions: OptionExample[] = [
  {
    id: '0',
    label: 'Left-Handed',
    value: 'LT',
  },
  {
    id: '1',
    label: 'Right-Handed',
    value: 'RT',
  },
] as const;

export const backhandOptions: OptionExample[] = [
  {
    id: '0',
    label: 'One-Handed',
    value: 'One-Handed',
  },
  {
    id: '1',
    label: 'Two-Handed',
    value: 'Two-Handed',
  },
] as const;

export const showToPublicOptions = [
  {
    id: '0',
    label: formatText({ text: 'Yes', role: TextRole.RadioLabel }),
    value: true,
  },
  {
    id: '1',
    label: formatText({ text: 'No', role: TextRole.RadioLabel }),
    value: false,
  },
] as const satisfies readonly OptionExample[];

// Values are written to the `unsubscribe*` fields, so "No" means unsubscribed (true).
export const emailOptions = [
  {
    id: '0',
    label: formatText({ text: 'No', role: TextRole.RadioLabel }),
    value: true,
  },
  {
    id: '1',
    label: formatText({ text: 'Yes', role: TextRole.RadioLabel }),
    value: false,
  },
] as const satisfies readonly OptionExample[];

export const hideBackToTopOptions = [
  {
    id: '0',
    label: formatText({ text: 'No', role: TextRole.RadioLabel }),
    value: false,
  },
  {
    id: '1',
    label: formatText({ text: 'Yes', role: TextRole.RadioLabel }),
    value: true,
  },
] as const satisfies readonly OptionExample[];

export const convertCentimetersToFeetAndInches = (
  centimeters: number
): { feet: number; inches: number } => {
  const totalInches = centimeters / 2.54;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches - feet * 12);

  if (inches === 12) {
    feet += 1;
    inches = 0;
  }

  return { feet, inches };
};

export const convertFeetAndInchesToCentimeters = (feet: number, inches: number): number => {
  const totalInches = Number(feet * 12) + Number(inches);
  const centimeters = Number(totalInches * 2.54);
  return Math.floor(centimeters);
};

export const convertKgToLbs = (kg: number) => Math.round(Number(kg * 2.20462));
export const convertLbsToKg = (lbs: number) => Math.round(Number(lbs / 2.20462));

export const filterValidRatings = (ratings: Rating[]) => {
  const excludedTitles = ['Open', 'Pro', '1', '1.5', '5.5', '6', '6.5', '7', '7.5', '8'];

  return ratings.filter((item) => !excludedTitles.includes(item.title));
};

export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

export const removeEmojis = (str: string) => {
  return str.replace(
    /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F1E6}-\u{1F1FF}]/gu,
    ''
  );
};

export const handleEditProfileInput = (e: React.FormEvent<HTMLInputElement>) => {
  e.currentTarget.value = removeEmojis(e.currentTarget.value);
};

export const handleEditProfilePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  const pasted = e.clipboardData.getData('text');
  const clean = removeEmojis(pasted);

  if (pasted !== clean) {
    e.preventDefault();
    e.currentTarget.value += clean;
  }
};

export function getChangedFields<T extends object>(original: T, updated: T): Partial<T> {
  const changed: Partial<T> = {};

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  for (const key of Object.keys(updated) as (keyof T)[]) {
    if (JSON.stringify(original[key]) !== JSON.stringify(updated[key])) {
      changed[key] = updated[key];
    }
  }
  return changed;
}

/**
 * `mapUserToApiPayload` serialises country ids as strings, and the app already treats 0 as
 * "no country selected" (`selectedCountry?.id || 0`). Use it to actively clear a stored code.
 */
const UNSET_PHONE_COUNTRY_ID = '0';

export function prepareChangedPayload(
  original: User,
  modified: User
): Partial<UserV2> & { uuid: string } {
  const fullPayload = mapUserToApiPayload(modified);
  const originalPayload = mapUserToApiPayload(original);

  const changedPayload = getChangedFields(originalPayload, fullPayload);

  const result: Partial<UserV2> & { uuid: string } = {
    ...changedPayload,
    uuid: original.uuid,
  };

  if ('phone' in changedPayload) {
    result.phoneCountryId = fullPayload.phoneCountryId;
  }

  // Minors are allowed to save without a phone, and the country select auto-defaults, so a
  // country code must never be persisted with no number behind it.
  //
  // When the minor has just cleared their number, send the unset sentinel rather than omitting
  // the key: omitting it leaves a previously-saved code orphaned in the record. When the phone
  // was already empty and untouched, send nothing at all — a parent-created minor is rejected by
  // the API for fields it did not change, so the payload stays minimal.
  if (original.isMinor && !fullPayload.phone) {
    if ('phone' in changedPayload) {
      result.phoneCountryId = UNSET_PHONE_COUNTRY_ID;
    } else {
      delete result.phoneCountryId;
    }
  }

  if ('emergencyContactPhone' in changedPayload) {
    result.emergencyContactPhoneCountryId = fullPayload.emergencyContactPhoneCountryId;
  }

  return result;
}

export const returnErrorMessage = (error: unknown) => {
  if (error instanceof HttpError && error.message.includes('phone number already exists')) {
    return 'Phone number already exists. Try again.';
  }
  return 'Something went wrong. Try again.';
};
